import { styled, useTheme } from '@automata/mui-theme';
import Box, { BoxProps } from '@mui/material/Box';

const BORDER_ELEMENT_CLASS = 'border-element';

type Props = Omit<BoxProps, 'borderColor' | 'bgcolor'> & {
  isSelected?: boolean;
  borderColor?: string;
  hasError?: boolean;
  bgcolor?: string;
  padding?: number;
};

const Wrap = styled(Box)<{ bgcolor?: string; padding?: number }>`
  position: relative;
  border-radius: 5px;
  padding: ${(p) => p.padding ?? 1}rem;
  margin: 0.25rem;
  background-color: ${(p) => p.bgcolor};
  &.has-error {
    background-color: ${(p) => p.theme.palette.error.light};
    &:hover {
      & .${BORDER_ELEMENT_CLASS} {
        border-color: ${(p) => p.theme.palette.error.dark};
      }
    }
  }
  &:hover {
    & .${BORDER_ELEMENT_CLASS} {
      border-color: ${(p) => p.theme.palette.neutral[900]};
    }
  }
`;

const BorderElement = styled('div')<{ borderColor?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  border: 2px solid;
  border-color: ${(p) => p.borderColor};
  pointer-events: none;
  transition-property: top, left, right, bottom, border;
  transition-duration: 0.2s;
  &.selected {
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 4px solid ${(p) => p.theme.palette.neutral[900]};
  }
  &.has-error {
    border-color: ${(p) => p.theme.palette.error.main};
    &.selected {
      border-color: ${(p) => p.theme.palette.error.dark};
    }
  }
`;

const NodeBackground = ({
  children,
  borderColor,
  bgcolor,
  hasError = false,
  isSelected = false,
  padding = 1,
  ...restBoxProps
}: Props): JSX.Element => {
  const theme = useTheme();
  const bgColorWithDefault = bgcolor ?? theme.palette.common.white;
  const borderColorWithDefault = borderColor ?? theme.palette.neutral[400];

  return (
    <Wrap
      bgcolor={bgColorWithDefault}
      padding={padding}
      {...restBoxProps}
      className={[restBoxProps.className, hasError ? 'has-error' : ''].join(
        ' '
      )}
    >
      <BorderElement
        borderColor={borderColorWithDefault}
        className={[
          BORDER_ELEMENT_CLASS,
          isSelected ? 'selected' : '',
          hasError ? 'has-error' : '',
        ].join(' ')}
      />
      {children}
    </Wrap>
  );
};

export default NodeBackground;
